import { Component } from '@theme/component';
import { CURRENCY_DECIMALS, DEFAULT_CURRENCY_DECIMALS, convertMoney, formatMoney } from '@theme/money-formatting';

/**
 * Renders a price with a shop-currency discount taken off it.
 *
 * The price is already in the market's currency; the discount comes from configuration and is
 * quoted in the shop's, so it needs `Shopify.currency.rate` applied before the two can be
 * combined. Liquid has no exchange rate, hence doing it here.
 *
 * @property {string} data-discount - The discount in the shop's currency, in major units (`20.0`)
 * @property {string} data-current-price-unformatted - The price in major units, from `money_amount`
 * @property {string} [data-amount] - A shop-currency amount to render on its own, in major units,
 * with nothing to subtract it from — a fixed offer price like "any 3 for 33". Takes precedence
 * over the pair above, which describe a discount off a price.
 * @property {string} data-format - Shopify's money format template, e.g. `{{ shop.money_format }}`
 * @property {string} [data-pending] - Set by Liquid when the market's currency is not the shop's,
 * so the server-rendered price is wrong until this runs. Removed once it has.
 *
 * @extends {Component}
 *
 * @example
 * <discount-price
 *   data-discount="20.0"
 *   data-current-price-unformatted="{{ variant.price | money_amount }}"
 *   data-format="{{ shop.money_format }}"
 * >{{ variant.price | money }}</discount-price>
 */
class DiscountPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#render();
  }

  /**
   * Replaces the server-rendered price with the discounted one. Bails on anything missing or
   * unparseable, leaving the Liquid price in place — so `data-pending` clears either way rather
   * than hiding the price for good.
   * @returns {void}
   */
  #render() {
    try {
      const { discount, currentPriceUnformatted, format, amount } = this.dataset;
      const currency = window.Shopify?.currency?.active;

      if (!format || !currency) return;

      // `money_amount` is major units and formatMoney takes minor, so scale by the currency's own
      // precision — two for USD, none for JPY.
      const precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
      const render = (/** @type {number} */ major) =>
        (this.textContent = formatMoney(Math.round(major * Math.pow(10, precision)), format, currency));

      // A standalone offer price: convert it and stop, there is nothing to take it off.
      if (amount != null) {
        if (Number.isFinite(Number(amount))) render(convertMoney(Number(amount)));
        return;
      }

      const current = Number(currentPriceUnformatted);
      if (!Number.isFinite(current) || !Number.isFinite(Number(discount))) return;

      render(Math.max(0, current - convertMoney(Number(discount))));
    } finally {
      this.removeAttribute('data-pending');
    }
  }
}

if (!customElements.get('discount-price')) {
  customElements.define('discount-price', DiscountPrice);
}
